<template>
  <NuxtLayout
    v-if="staffInfo"
    :name="setLayout"
  >
    <div class="tourleader-page pb-12">
      <PrettyTabsNav
        v-if="hasTabs"
        class="pretty-tabs-layout-column pretty-tabs-layout-md-row"
        :tabs="navTabs"
        :locale-urls="localeUrlList"
        slug="slug"
        @on-tab-change="onTabChange"
      />
      <div class="pt-4 pb-12">
        <template v-if="activeTab === TABS.reviews && hasReviews">
          <TourleaderPageReviews
            :id="id"
            :reviews="reviews"
          />
        </template>
        <template v-else>
          <TourleaderPageAbout
            :id="id"
            :info="staffInfo"
            :reviews="reviews"
          />
        </template>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
import { callWithNuxt } from '#app'

const nuxtApp = useNuxtApp()
const { urls: localeURLs } = useLocale()
const localeStore = useLocaleStore()
const rootStore = useRootStore()
const { t } = useI18n()
const route = useRoute()
const router = useRouter()

const { public: { API_URL } } = useRuntimeConfig()
const { setPageBanner } = useBanner()

const getIdByUrl = (params) => {
  let id
  const urlParts = `${params?.id}`.split('-')
  if (!isNaN(urlParts[urlParts.length - 1])) {
    id = urlParts[urlParts.length - 1]
  } else {
    const numericParts = urlParts.filter(x => !isNaN(x))
    if (numericParts.length > 0) {
      id = numericParts[0]
    } else {
      id = params?.id
    }
  }
  return id
}

const getTourleaderSlug = (staff) => {
  let slug = staff?.nid
  if (!slug && staff) {
    slug = [staff?.url_safe_name || toSnakeCase(staff?.name), staff?.id]
      .filter(Boolean).join('-')
  }
  return slug
}

const setLayout = computed(() => {
  return isSol() ? 'charter-page' : 'banner-default'
})

const TABS = {
  about: 'about',
  reviews: 'reviews',
}

const activeTab = ref(TABS.about)

const localeUrlList = computed(() => {
  return localeURLs?.tourleaderSlug
})

const hasReviews = computed(() => {
  return (reviews || []).length > 0
})

const navTabs = computed(() => {
  const rootUrl = localeURLs?.tourleader
  const slug = getTourleaderSlug(staffInfo)
  const baseUrl = `${rootUrl}/${slug}`
  return [
    {
      text: 'tourleader.tab.about',
      to: baseUrl,
      localeUrlKey: TABS.about,
    },
    ...(hasReviews.value > 0 ? [{
      text: 'tourleader.tab.reviews',
      to: `${baseUrl}${localeUrlList.value?.[TABS.reviews]}`,
      localeUrlKey: TABS.reviews,
    }] : []),
  ]
})

const hasTabs = computed(() => {
  return navTabs.value.length > 1
})

const { error, data } = await useAsyncData(async () => {
  try {
    const params = route.params
    const id = getIdByUrl(params)
    const urlParts = `${params.id}`.split('-')
    const safeLocale = localeStore.getTemporaryFallbackLocale

    const { data: result } = await apiFetch(`/${safeLocale}/staff/${id}`)
    const staff = result

    if (urlParts.length === 1) {
      const slug = getTourleaderSlug(staff)
      const link = `${localeStore.urls.tourleader}/${slug}`

      return callWithNuxt(nuxtApp, navigateTo, [link])
    }

    let videoUrl = null
    if (staff?.video) {
      videoUrl = `${API_URL}rolfsapi/v2/web/${safeLocale}/${staff?.video?.id}`
    }

    let bannerImg
    const images = (staff?.upcoming_triptypes || [])
      .map(x => x?.main_picture_cloudinary)
      .filter(Boolean)
    if (images.length > 0) {
      bannerImg = images[0]
    } else {
      const imageInfo = await rootStore.fetchHeroImage({ page: 'tourleader', onlyFetch: true })
      bannerImg = imageInfo?.picture_cloudinary
    }

    setPageBanner({
      title: staff.name,
      subtitle: t('tourleaderSubtitle'),
      size: 'small',
      background: {
        image: {
          url: bannerImg,
        },
      },
    })

    const urls = localeStore.urls?.tourleaderSlug
    const tab = findActiveSlugKey(urls, params, 'slug')

    let reviews = []
    try {
      const { data: result } = await apiFetch(`/${localeStore.getTemporaryFallbackLocale}/reviews-for-tourleader/${id}?page=1&pageNum=10`)
      reviews = result
    } catch {
      // do nothing
    }

    activeTab.value = tab

    return {
      staffInfo: {
        ...staff,
        videoUrl,
      },
      reviews,
      id,
    }
  } catch (e) {
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  navigateTo(localeStore.urls.tourleader)
}

const { staffInfo, reviews, id } = data.value

const tab = (navTabs.value || []).find(x => x?.localeUrlKey === activeTab.value) || navTabs.value?.[0]

const url = `${localeURLs.BASE_URL}${tab?.to}`

const titleSuffix = activeTab.value !== TABS.about ? `- ${t(tab?.text)}` : ''
const { $isSol } = useNuxtApp()

useHead({
  title: () => `${staffInfo?.name}${titleSuffix}`,

  link: () => activeTab.value === TABS.about ? [{
    rel: 'canonical',
    href: () => url,
  }] : [{
    rel: 'canonical',
    href: () => url,
  }],

  meta: [
    {
      hid: 'description',
      name: 'description',
      content: () => staffInfo.info ? removeTags(staffInfo.info).trim() : staffInfo?.info,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => `${staffInfo?.name} - ${t('siteTitleBrand')}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => formatCloudinaryImagePath(staffInfo?.picture_cloudinary, {
        forceJPG: true,
        isSol: $isSol,
      }),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => staffInfo.info ? removeTags(staffInfo.info).trim() : staffInfo?.info,
    },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => url,
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Person',
        'name': staffInfo?.name,
        'image': formatCloudinaryImagePath(staffInfo?.picture_cloudinary, {
          forceJPG: true,
          isSol: $isSol,
        }),
        'description': staffInfo.info ? removeTags(staffInfo.info).trim() : staffInfo?.info,
      }),
    },
    reviews.length === 0 ? null : {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Review',
        'author': t('anonym'),
        'reviewRating': {
          '@type': 'Rating',
          'ratingValue': reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length,
          'worstRating': '0',
          'bestRating': '5',
        },
        'itemReviewed': reviews.map((review) => ({
          '@type': 'product',
          'name': review.tripname,
          'brand': {
            '@type': 'Brand',
            'name': t('siteTitleBrand'),
          },
          'image': formatCloudinaryImagePath(staffInfo?.picture_cloudinary, {
            forceJPG: true,
            isSol: $isSol,
          }),
          'author': review.author || t('anonym'),
          'description': review.text,
          'gtin13': review.gtin13 || 'default-gtin13',
          'sku': review.sku || 'default-sku',
          'review': {
            '@type': 'Review',
            'author': {
              '@type': 'Person',
              'name': review.author || t('anonym'),
            },
            'reviewBody': review.text,
            'reviewRating': {
              '@type': 'Rating',
              'ratingValue': review.rating,
              'worstRating': '0',
              'bestRating': '5',
            },
          },
        })),
      }),
    },
  ],
})

const onTabChange = (newActiveTab) => {
  activeTab.value = newActiveTab
}
</script>

<style lang="scss">
.tourleader-page {
  h4 {
    letter-spacing: 0.5px;
    font-weight: 500;
  }
  .product__testimonials__item {
    background: theme('colors.lightest-gray');
  }
}
</style>
